import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Alert,
  Table,
  Spinner,
  Label,
  FormGroup,
} from 'reactstrap';
import { buttonVariants, Button as CustomButton } from '@components/Button';
// import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Input } from '@components/Input';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import firebase from '../../config/firebase.config';
import httpClient from '../../config/httpClient';
import './CreateEmployees.scss';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import styles from './CreateEmployees.module.scss';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { TabBar } from '@/components/TabBar';
import { useTimezones } from '@/hooks/useTimezones';
import { useUserCompanies } from '@/hooks/useUserCompanies';
import { useUserTeamsForCompany } from '@/hooks/useUserTeamsForCompany';
import { useStealthMode } from '@/hooks/useStealthMode';
import { useEmployeePageCompanyTeamUrlSync } from '@/hooks/useEmployeePageUrlSync';
const tabBarOptions = [
  {
    label: 'Upload Excel',
    value: 'uploadExcel',
  },
  {
    label: 'Manual Entry',
    value: 'manualEntry',
  },
];
export const CreateEmployeesComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const authState = useSelector((state) => ({ ...state.auth }));
  const userProfile = authState.profile;
  const companyList = userProfile?.companyList || [];
  const userUID = userProfile?.id;
  const activeCompanyId = userProfile?.activeCompany?.companyId;
  const activeTeamId = useSelector(
    (s) => s.team?.active?.activeTeamid ?? s.team?.active?.activeTeamId
  );

  // State
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const {
    companyOptions,
    loading: loadingCompanies,
    error: companiesError,
  } = useUserCompanies(userUID);
  const {
    teamOptions,
    loading: loadingTeams,
    error: teamsError,
  } = useUserTeamsForCompany(userUID, selectedCompany?.value);

  useEmployeePageCompanyTeamUrlSync({
    companyOptions,
    teamOptions,
    selectedCompany,
    selectedTeam,
    setSelectedCompany,
    setSelectedTeam,
    history,
    location,
    defaultCompanyId: activeCompanyId,
    defaultTeamId: activeTeamId,
    teamDefaultOnlyWhenCompanyId: activeCompanyId,
  });

  const { stealthMode, loading: stealthLoading } = useStealthMode();
  const [activeTab, setActiveTab] = useState(tabBarOptions[0].value);

  // Excel upload state
  const [excelFile, setExcelFile] = useState(null);
  const [excelFileName, setExcelFileName] = useState('');

  // Manual form state
  const [employees, setEmployees] = useState([
    { email: '', name: '', timeZone: '', role: '', deskId: '', employeeId: '' },
  ]);

  // Results state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Timezone options
  const timezoneOptions = useTimezones();
  const roleOptions = [
    { label: 'Member', value: 'Member' },
    { label: 'Manager', value: 'Manager' },
    { label: 'Admin', value: 'Admin' },
  ];

  // Download Excel template
  const downloadExcelTemplate = () => {
    const templateData = [
      {
        email: 'john.doe@company.com',
        name: 'John Doe',
        timeZone: 'America/New_York',
        role: 'Member',
        deskId: 'DESK-001',
        employeeId: 'EMP-001',
      },
      {
        email: 'jane.smith@company.com',
        name: 'Jane Smith',
        timeZone: 'Europe/London',
        role: 'Manager',
        deskId: '',
        employeeId: '',
      },
    ];
    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Employees');

    // Add Instructions sheet
    const instructions = [
      {
        Column: 'email',
        Required: 'Yes',
        Description: 'Valid email address for each employee',
      },
      {
        Column: 'name',
        Required: 'Yes',
        Description: 'Full name of the employee',
      },
      {
        Column: 'timeZone',
        Required: 'Yes',
        Description: 'IANA timezone (e.g., America/New_York, Asia/Kolkata)',
      },
      {
        Column: 'role',
        Required: 'Yes',
        Description: 'Member, Manager, or Admin',
      },
      {
        Column: 'deskId',
        Required: 'No',
        Description: 'Optional desk identifier',
      },
      {
        Column: 'employeeId',
        Required: 'No',
        Description: 'Optional employee ID',
      },
    ];
    const instructionsSheet = XLSX.utils.json_to_sheet(instructions);
    XLSX.utils.book_append_sheet(workbook, instructionsSheet, 'Instructions');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, 'employee_template.xlsx');
  };

  // Check if user is Admin
  useEffect(() => {
    const isAdmin =
      userProfile?.activeCompany?.role === 'Admin' ||
      companyList.some((company) => company.role === 'Admin');

    if (!isAdmin && !authState.loading) {
      setError('Access denied. Only Admin users can create employees.');
    }
  }, [userProfile, companyList, authState.loading]);

  useEffect(() => {
    if (!selectedCompany?.value) {
      setSelectedTeam(null);
    }
  }, [selectedCompany?.value]);

  // Handle Excel file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];

    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      setError(
        'Invalid file type. Please upload an Excel file (.xlsx or .xls)'
      );
      return;
    }

    // Validate file size (20MB)
    const maxSize = 20 * 1024 * 1024; // 20MB in bytes
    if (file.size > maxSize) {
      setError(
        'File size exceeds 20MB limit. Please split the file or use manual entry.'
      );
      return;
    }

    setExcelFile(file);
    setExcelFileName(file.name);
    setError(null);
  };

  // Clear uploaded Excel file
  const clearExcelFile = () => {
    setExcelFile(null);
    setExcelFileName('');
    const fileInput = document.getElementById('excel-file-input');
    if (fileInput) fileInput.value = '';
  };

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result;
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Handle manual form - add employee row
  const addEmployeeRow = () => {
    setEmployees([
      ...employees,
      {
        email: '',
        name: '',
        timeZone: '',
        role: '',
        deskId: '',
        employeeId: '',
      },
    ]);
  };

  // Handle manual form - remove employee row
  const removeEmployeeRow = (index) => {
    const newEmployees = employees.filter((_, i) => i !== index);
    setEmployees(newEmployees);
  };

  // Handle manual form - update employee field
  const updateEmployeeField = (index, field, value) => {
    const newEmployees = [...employees];
    newEmployees[index][field] = value;
    setEmployees(newEmployees);
  };

  // Validate employees data (manual entry)
  const validateEmployees = (employeesData) => {
    const errors = [];
    const emailSet = new Set();
    const deskIdSet = new Set();

    employeesData.forEach((emp, index) => {
      if (!emp.email) errors.push(`Row ${index + 1}: Email is required`);
      if (!emp.name) errors.push(`Row ${index + 1}: Name is required`);
      if (!emp.timeZone) errors.push(`Row ${index + 1}: TimeZone is required`);
      if (!emp.role) errors.push(`Row ${index + 1}: Role is required`);

      if (emp.email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emp.email)) {
          errors.push(`Row ${index + 1}: Invalid email format`);
        }
        if (emailSet.has(emp.email.toLowerCase())) {
          errors.push(`Row ${index + 1}: Duplicate email in form`);
        }
        emailSet.add(emp.email.toLowerCase());
      }

      if (emp.deskId != null && String(emp.deskId).trim() !== '') {
        const trimmedDeskId = String(emp.deskId).trim();
        if (deskIdSet.has(trimmedDeskId)) {
          errors.push(`Row ${index + 1}: Duplicate deskId (${trimmedDeskId})`);
        }
        deskIdSet.add(trimmedDeskId);
      }
    });

    return errors;
  };

  // Parse Excel and validate duplicate deskIds (Excel upload)
  const validateExcelDeskIds = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { raw: false });
          if (!rows || rows.length === 0) {
            resolve({ valid: true });
            return;
          }
          const deskIdHeaderAliases = ['deskid', 'desk id', 'desk_id'];
          const headerKeys = Object.keys(rows[0]);
          const deskIdKey = headerKeys.find(
            (k) =>
              deskIdHeaderAliases.includes(k.toLowerCase().trim()) ||
              k.trim() === 'deskId'
          );
          const deskIdSet = new Set();
          for (let i = 0; i < rows.length; i++) {
            const raw = deskIdKey ? rows[i][deskIdKey] : undefined;
            const value = raw != null ? String(raw).trim() : '';
            if (value === '') continue;
            if (deskIdSet.has(value)) {
              resolve({
                valid: false,
                message: `Duplicate deskId in file: "${value}" appears in multiple rows (e.g. row ${
                  i + 2
                }).`,
              });
              return;
            }
            deskIdSet.add(value);
          }
          resolve({ valid: true });
        } catch (err) {
          resolve({
            valid: false,
            message: `Could not parse file for validation: ${err.message}`,
          });
        }
      };
      reader.onerror = () =>
        resolve({ valid: false, message: 'Failed to read file' });
      reader.readAsArrayBuffer(file);
    });
  };

  // Submit form (stealth on: provision accounts directly; stealth off: email invitations)
  const handleSubmit = async () => {
    setError(null);
    setResult(null);
    if (
      !userProfile?.subscription?.isTrialActive &&
      userProfile?.subscription?.status !== 'active'
    ) {
      setError(
        'Your subscription is not active. Please activate your subscription to create employees.'
      );
      return;
    }
    if (!selectedCompany?.value || !selectedTeam?.value) {
      setError('Please select both company and team');
      return;
    }

    if (stealthLoading) {
      setError('Company settings are still loading. Please try again shortly.');
      return;
    }

    const useInvitationFlow = !stealthMode;

    setLoading(true);

    try {
      // Get Firebase ID token
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      const idToken = await user.getIdToken();

      let requestBody;

      if (activeTab === 'uploadExcel') {
        // Excel upload
        if (!excelFile) {
          setError('Please select an Excel file');
          setLoading(false);
          return;
        }

        const excelDeskIdValidation = await validateExcelDeskIds(excelFile);
        if (!excelDeskIdValidation.valid) {
          setError(excelDeskIdValidation.message);
          setLoading(false);
          return;
        }

        const fileBase64 = await fileToBase64(excelFile);
        requestBody = {
          companyId: selectedCompany?.value,
          teamId: selectedTeam?.value,
          fileBase64,
          fileName: excelFileName,
        };
      } else {
        // Manual form
        // Filter out empty rows
        const validEmployees = employees.filter(
          (emp) => emp.email || emp.name || emp.timeZone || emp.role
        );

        if (validEmployees.length === 0) {
          setError('Please add at least one employee');
          setLoading(false);
          return;
        }

        // Validate
        const validationErrors = validateEmployees(validEmployees);
        if (validationErrors.length > 0) {
          setError(validationErrors.join('\n'));
          setLoading(false);
          return;
        }

        requestBody = {
          companyId: selectedCompany?.value,
          teamId: selectedTeam?.value,
          source: 'form',
          employees: validEmployees,
          invitedByName: userProfile?.name,
        };
      }

      const endpoint = useInvitationFlow
        ? '/sendAddEmployeeInvites'
        : '/createEmployeesFromExcel';

      const response = await httpClient.post(endpoint, requestBody, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const data = {
        ...response.data,
        _flow: useInvitationFlow ? 'invite' : 'direct',
      };

      setResult(data);

      if (!useInvitationFlow) {
        if (data.success && data.data?.created?.length) {
          const toSend = data.data.created.filter(
            (emp) => emp.role === 'Admin' || emp.role === 'Manager'
          );
          for (const emp of toSend) {
            try {
              await firebase.auth().sendPasswordResetEmail(emp.email);
            } catch (e) {
              console.warn('Could not send password reset to', emp.email, e);
            }
          }
        }
      }

      const shouldResetForm = (() => {
        if (!data.success || !data.data?.stats) return false;
        if (useInvitationFlow) {
          return data.data.stats.errors === 0;
        }
        return data.data.stats.failed === 0;
      })();

      if (shouldResetForm) {
        if (activeTab === 'uploadExcel') {
          setExcelFile(null);
          setExcelFileName('');
          const fileInput = document.getElementById('excel-file-input');
          if (fileInput) fileInput.value = '';
        } else {
          setEmployees([
            {
              email: '',
              name: '',
              timeZone: '',
              role: '',
              deskId: '',
              employeeId: '',
            },
          ]);
        }
      }
    } catch (err) {
      console.error('Error creating employees:', err);
      const data = err.response?.data;
      let errorMessage = 'An error occurred while creating employees';
      if (data) {
        errorMessage = data.message || errorMessage;
        if (
          data.errors &&
          Array.isArray(data.errors) &&
          data.errors.length > 0
        ) {
          const details = data.errors.map(
            (e) =>
              `Row ${e.row}${
                e.email && e.email !== 'N/A' ? ` (${e.email})` : ''
              }: ${e.error}`
          );
          errorMessage = `${errorMessage}\n\n${details.join('\n')}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Check if user is Admin
  const isAdmin =
    userProfile?.activeCompany?.role === 'Admin' ||
    companyList.some((company) => company.role === 'Admin');

  if (!isAdmin && !authState.loading) {
    return (
      <Container className="mt-5">
        <Alert color="danger">
          <h4>Access Denied</h4>
          <p>Only Admin users can create employees.</p>
        </Alert>
      </Container>
    );
  }

  const uploadExcelTab = () => {
    return (
      <div className={styles.uploadExcelTab}>
        <div className={styles.downloadTemplateContainer}>
          <Text
            variant={textVariantsEnum.Text_13}
            fontWeight={fontWeightEnum.Normal}
          >
            Download template
          </Text>
          <Text
            variant={textVariantsEnum.Text_13}
            fontWeight={fontWeightEnum.Normal}
            onClick={downloadExcelTemplate}
          >
            <i className="fas fa-download mr-2" />
            Download Excel Template
          </Text>
          <Text
            variant={textVariantsEnum.Text_13}
            fontWeight={fontWeightEnum.Normal}
          >
            Template includes column headers and sample rows. See the
            "Instructions" sheet for column details.
          </Text>
        </div>
        <div className={styles.createEmployeesFileUploadContainer}>
          <Label>Excel File *</Label>
          <div className={styles.createEmployeesFileUpload}>
            <input
              id="excel-file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className={styles.createEmployeesFileInputHidden}
            />
            <label
              htmlFor="excel-file-input"
              className={styles.createEmployeesFileLabel}
            >
              Choose file
            </label>
            <span
              className={styles.createEmployeesFileName + ' text-muted ml-2'}
            >
              {excelFileName || 'No file chosen'}
            </span>
            {excelFileName && (
              <Button
                type="button"
                color="danger"
                outline
                size="sm"
                onClick={clearExcelFile}
                className={styles.createEmployeesRemoveFileBtn + ' ml-2'}
              >
                <i className="fas fa-times mr-1" />
                Remove
              </Button>
            )}
          </div>
          <small
            className={
              styles.createEmployeesFileSize + ' text-muted d-block mt-2'
            }
          >
            Accepted formats: .xlsx, .xls (Max 20MB)
          </small>
          {excelFileName && (
            <Alert
              color="info"
              className={styles.createEmployeesSelectedFile + ' mt-2 mb-0'}
            >
              Selected file: <strong>{excelFileName}</strong>
            </Alert>
          )}
        </div>
      </div>
    );
  };
  const manualEntryTab = () => {
    return (
      <div className={styles.manualEntryTabContainer}>
        <div className="manual-entry-toolbar mb-3">
          <CustomButton
            text="Add Employee"
            variant={buttonVariants.primary}
            size="sm"
            onClick={addEmployeeRow}
            disabled={!selectedCompany?.value || !selectedTeam?.value}
            className="add-employee-btn"
          />
        </div>

        <div className="table-responsive create-employees-table-wrapper">
          <Table striped bordered className="create-employees-table">
            <thead>
              <tr>
                <th className="col-email">Email *</th>
                <th className="col-name">Name *</th>
                <th className="col-timezone">TimeZone *</th>
                <th className="col-role">Role *</th>
                <th className="col-deskid">DeskId</th>
                <th className="col-employeeid">EmployeeId</th>
                {employees.length > 1 && <th className="col-remove">Remove</th>}
              </tr>
            </thead>
            <tbody>
              {employees.map((emp, index) => (
                <tr key={index}>
                  <td className="col-email">
                    <Input
                      value={emp.email}
                      className={styles.manualEntryInput}
                      onChange={(e) =>
                        updateEmployeeField(index, 'email', e.target.value)
                      }
                      placeholder="email@example.com"
                    />
                  </td>
                  <td className="col-name">
                    <Input
                      value={emp.name}
                      onChange={(e) =>
                        updateEmployeeField(index, 'name', e.target.value)
                      }
                      className={styles.manualEntryInput}
                      placeholder="Full Name"
                    />
                  </td>
                  <td className="col-timezone">
                    <Dropdown
                      value={
                        emp.timeZone
                          ? { label: emp.timeZone, value: emp.timeZone }
                          : null
                      }
                      options={timezoneOptions}
                      onChange={(option) =>
                        updateEmployeeField(index, 'timeZone', option.value)
                      }
                      placeholder="Select TimeZone"
                    />
                  </td>
                  <td className="col-role">
                    <Dropdown
                      value={
                        emp.role ? { label: emp.role, value: emp.role } : null
                      }
                      options={roleOptions}
                      onChange={(option) => {
                        updateEmployeeField(index, 'role', option.value);
                      }}
                      placeholder="Select Role"
                    />
                  </td>
                  <td className="col-deskid">
                    <Input
                      value={emp.deskId}
                      onChange={(e) =>
                        updateEmployeeField(index, 'deskId', e.target.value)
                      }
                      className={styles.manualEntryInput}
                      placeholder="Optional"
                    />
                  </td>
                  <td className="col-employeeid">
                    <Input
                      value={emp.employeeId}
                      onChange={(e) =>
                        updateEmployeeField(index, 'employeeId', e.target.value)
                      }
                      className={styles.manualEntryInput}
                      placeholder="Optional"
                    />
                  </td>
                  {employees.length > 1 && (
                    <td className="col-remove">
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => removeEmployeeRow(index)}
                        className="remove-row-btn"
                      >
                        <i className="fas fa-trash-alt mr-1" />
                        Remove
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };
  return (
    <>
      <div className={styles.createEmployeesPage}>
        <Text
          variant={textVariantsEnum.Header}
          fontWeight={fontWeightEnum.Semibold}
        >
          Add Employee
        </Text>
        <div className={styles.createEmployeesFormContainer}>
          <div className={styles.createEmployeesFormDropdownContainer}>
            <Dropdown
              label="Company *"
              value={selectedCompany}
              selectStyles={borderedSelectStyles}
              options={companyOptions}
              onChange={(option) => {
                setSelectedCompany(option);
                setSelectedTeam(null);
                setResult(null);
                setError(null);
              }}
              placeholder={
                loadingCompanies ? 'Loading companies...' : 'Select Company'
              }
            />
            <Dropdown
              label="Department *"
              value={selectedTeam}
              selectStyles={borderedSelectStyles}
              options={teamOptions}
              onChange={(option) => {
                setSelectedTeam(option);
                setResult(null);
                setError(null);
              }}
              placeholder={loadingTeams ? 'Loading teams...' : 'Select Team'}
              disabled={!selectedCompany || loadingTeams}
            />
          </div>
          <div className={styles.createEmployeesTabBarContainer}>
            <TabBar
              tabs={tabBarOptions}
              activeTab={activeTab}
              onTabClick={(tab) => {
                setActiveTab(tab);
                setResult(null);
                setError(null);
              }}
            />
            {activeTab === 'uploadExcel' ? uploadExcelTab() : manualEntryTab()}

            {(error || companiesError || teamsError) && (
              <Alert color="danger" className="mt-3">
                <pre style={{ whiteSpace: 'pre-wrap' }}>
                  {error || companiesError || teamsError || ''}
                </pre>
              </Alert>
            )}
            {!stealthLoading && !stealthMode && (
              <Alert color="info" className="mt-3 mb-0">
                People you add here receive an email invitation. They sign up
                and accept the invite to join the selected team (or the company,
                if the role is Admin).
              </Alert>
            )}
            {!stealthLoading && (
              <Alert color="secondary" className="mt-2 mb-0">
                Add Employee is for creating new employees only. If this email
                already exists, use Employee List - Add to another team.
              </Alert>
            )}

            <CustomButton
              text={
                stealthLoading
                  ? 'Loading…'
                  : stealthMode
                    ? 'Add employees'
                    : 'Send invitations'
              }
              variant={buttonVariants.primary}
              loading={loading}
              size="sm"
              onClick={handleSubmit}
              disabled={
                !selectedCompany?.value ||
                !selectedTeam?.value ||
                stealthLoading
              }
              className="mt-3"
            />
            {/* Results Display */}
            {result && (
              <div className="mt-4">
                <Alert color={result.success ? 'success' : 'warning'}>
                  <h5>{result.message}</h5>
                  {result.data &&
                    result.data.stats &&
                    result._flow !== 'invite' && (
                      <div className="mt-2">
                        <p>
                          <strong>Total:</strong> {result.data.stats.total} |{' '}
                          <strong>Created:</strong> {result.data.stats.created}{' '}
                          | <strong>Failed:</strong> {result.data.stats.failed}
                        </p>
                      </div>
                    )}
                  {result.data &&
                    result.data.stats &&
                    result._flow === 'invite' && (
                      <div className="mt-2">
                        <p>
                          <strong>Total:</strong> {result.data.stats.total} |{' '}
                          <strong>Sent or updated:</strong>{' '}
                          {result.data.stats.sentOrUpdated} |{' '}
                          <strong>Skipped:</strong> {result.data.stats.skipped}{' '}
                          | <strong>Email issues:</strong>{' '}
                          {result.data.stats.partial} | <strong>Errors:</strong>{' '}
                          {result.data.stats.errors}
                        </p>
                      </div>
                    )}
                </Alert>

                {result._flow !== 'invite' &&
                  result.data &&
                  result.data.created &&
                  result.data.created.length > 0 && (
                    <div className="mt-3">
                      <h5>Created Employees:</h5>
                      <Table striped bordered size="sm">
                        <thead>
                          <tr>
                            <th>Row</th>
                            <th>Email</th>
                            <th>Name</th>
                            <th>UserId</th>
                            <th>License GUID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.data.created.map((emp, index) => (
                            <tr key={index}>
                              <td>{emp.row}</td>
                              <td>{emp.email}</td>
                              <td>{emp.name}</td>
                              <td>{emp.userId}</td>
                              <td>{emp.licenseGuid}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}

                {result._flow === 'invite' &&
                  result.data &&
                  Array.isArray(result.data.results) &&
                  result.data.results.length > 0 && (
                    <div className="mt-3">
                      <h5>Invitation results:</h5>
                      <Table striped bordered size="sm">
                        <thead>
                          <tr>
                            <th>Row</th>
                            <th>Email</th>
                            <th>Outcome</th>
                            <th>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.data.results.map((row, index) => (
                            <tr key={index}>
                              <td>{row.row}</td>
                              <td>{row.email}</td>
                              <td>{row.outcome}</td>
                              <td>{row.message}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}

                {result.data &&
                  result.data.errors &&
                  result.data.errors.length > 0 && (
                    <div className="mt-3">
                      <h5>Errors:</h5>
                      <Table striped bordered size="sm">
                        <thead>
                          <tr>
                            <th>Row</th>
                            <th>Email</th>
                            <th>Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.data.errors.map((err, index) => (
                            <tr key={index}>
                              <td>{err.row}</td>
                              <td>{err.email}</td>
                              <td>{err.error}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* <Container className="create-employees-page mt-4">
        <Row>
          <Col>
            <Card className="create-employees-card shadow-sm">
              <CardHeader className="create-employees-header">
                <h3 className="mb-1">Create Employees</h3>
                <p className="text-muted mb-0">
                  Upload an Excel file or enter employee details manually
                </p>
              </CardHeader>
              <CardBody>
                <TabView
                  activeIndex={activeTab}
                  onTabChange={(e) => {
                    setActiveTab(e.index);
                    setResult(null);
                    setError(null);
                  }}
                >
                  <TabPanel header="Upload Excel">
                    <div className="excel-upload-section">
                      <div className="template-download mb-3">
                        <Label className="d-block mb-2">
                          Download template
                        </Label>
                        <Button
                          color="secondary"
                          outline
                          size="sm"
                          onClick={downloadExcelTemplate}
                          className="d-inline-flex align-items-center"
                        >
                          <i className="fas fa-download mr-2" />
                          Download Excel Template
                        </Button>
                        <small className="text-muted d-block mt-2">
                          Template includes column headers and sample rows. See
                          the &quot;Instructions&quot; sheet for column details.
                        </small>
                      </div>
                      <div className="mb-3">
                        <Label>Excel File *</Label>
                        <div className="create-employees-file-upload">
                          <input
                            id="excel-file-input"
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            className="create-employees-file-input-hidden"
                          />
                          <label
                            htmlFor="excel-file-input"
                            className="create-employees-file-label"
                          >
                            Choose file
                          </label>
                          <span className="create-employees-file-name text-muted ml-2">
                            {excelFileName || 'No file chosen'}
                          </span>
                          {excelFileName && (
                            <Button
                              type="button"
                              color="danger"
                              outline
                              size="sm"
                              onClick={clearExcelFile}
                              className="create-employees-remove-file-btn ml-2"
                            >
                              <i className="fas fa-times mr-1" />
                              Remove
                            </Button>
                          )}
                        </div>
                        <small className="text-muted d-block mt-2">
                          Accepted formats: .xlsx, .xls (Max 20MB)
                        </small>
                        {excelFileName && (
                          <Alert color="info" className="mt-2 mb-0">
                            Selected file: <strong>{excelFileName}</strong>
                          </Alert>
                        )}
                      </div>
                    </div>
                  </TabPanel>

                  <TabPanel header="Manual Entry">
                    <div className="manual-entry-toolbar mb-3">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={addEmployeeRow}
                        disabled={!selectedCompany || !selectedTeam}
                        className="add-employee-btn"
                      >
                        <i className="fas fa-plus mr-2" />
                        Add Employee
                      </Button>
                    </div>

                    <div className="table-responsive create-employees-table-wrapper">
                      <Table
                        striped
                        bordered
                        className="create-employees-table"
                      >
                        <thead>
                          <tr>
                            <th className="col-email">Email *</th>
                            <th className="col-name">Name *</th>
                            <th className="col-timezone">TimeZone *</th>
                            <th className="col-role">Role *</th>
                            <th className="col-deskid">DeskId</th>
                            <th className="col-employeeid">EmployeeId</th>
                            {employees.length > 1 && (
                              <th className="col-remove">Remove</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {employees.map((emp, index) => (
                            <tr key={index}>
                              <td className="col-email">
                                <InputText
                                  value={emp.email}
                                  onChange={(e) =>
                                    updateEmployeeField(
                                      index,
                                      'email',
                                      e.target.value
                                    )
                                  }
                                  placeholder="email@example.com"
                                  className="w-100 p-inputtext-sm"
                                />
                              </td>
                              <td className="col-name">
                                <InputText
                                  value={emp.name}
                                  onChange={(e) =>
                                    updateEmployeeField(
                                      index,
                                      'name',
                                      e.target.value
                                    )
                                  }
                                  placeholder="Full Name"
                                  className="w-100 p-inputtext-sm"
                                />
                              </td>
                              <td className="col-timezone">
                                <Dropdown
                                  value={emp.timeZone}
                                  options={timezoneOptions}
                                  onChange={(e) =>
                                    updateEmployeeField(
                                      index,
                                      'timeZone',
                                      e.target.value
                                    )
                                  }
                                  placeholder="Select TimeZone"
                                  filter
                                  filterPlaceholder="Search timezone..."
                                  className="w-100"
                                />
                              </td>
                              <td className="col-role">
                                <Dropdown
                                  value={emp.role}
                                  options={roleOptions}
                                  onChange={(e) =>
                                    updateEmployeeField(
                                      index,
                                      'role',
                                      e.target.value
                                    )
                                  }
                                  placeholder="Select Role"
                                  className="w-100"
                                />
                              </td>
                              <td className="col-deskid">
                                <InputText
                                  value={emp.deskId}
                                  onChange={(e) =>
                                    updateEmployeeField(
                                      index,
                                      'deskId',
                                      e.target.value
                                    )
                                  }
                                  placeholder="Optional"
                                  className="w-100 p-inputtext-sm"
                                />
                              </td>
                              <td className="col-employeeid">
                                <InputText
                                  value={emp.employeeId}
                                  onChange={(e) =>
                                    updateEmployeeField(
                                      index,
                                      'employeeId',
                                      e.target.value
                                    )
                                  }
                                  placeholder="Optional"
                                  className="w-100 p-inputtext-sm"
                                />
                              </td>
                              {employees.length > 1 && (
                                <td className="col-remove">
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => removeEmployeeRow(index)}
                                    className="remove-row-btn"
                                  >
                                    <i className="fas fa-trash-alt mr-1" />
                                    Remove
                                  </Button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </TabPanel>
                </TabView>

                {error && (
                  <Alert color="danger" className="mt-3">
                    <pre style={{ whiteSpace: 'pre-wrap' }}>{error}</pre>
                  </Alert>
                )}

                <div className="mt-4 pt-3 border-top create-employees-submit">
                  <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={loading || !selectedCompany || !selectedTeam}
                    size="lg"
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Creating Employees...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-user-plus mr-2" />
                        Create Employees
                      </>
                    )}
                  </Button>
                </div>

                {result && (
                  <div className="mt-4">
                    <Alert color={result.success ? 'success' : 'warning'}>
                      <h5>{result.message}</h5>
                      {result.data && result.data.stats && (
                        <div className="mt-2">
                          <p>
                            <strong>Total:</strong> {result.data.stats.total} |{' '}
                            <strong>Created:</strong>{' '}
                            {result.data.stats.created} |{' '}
                            <strong>Failed:</strong> {result.data.stats.failed}
                          </p>
                        </div>
                      )}
                    </Alert>

                    {result.data &&
                      result.data.created &&
                      result.data.created.length > 0 && (
                        <div className="mt-3">
                          <h5>Created Employees:</h5>
                          <Table striped bordered size="sm">
                            <thead>
                              <tr>
                                <th>Row</th>
                                <th>Email</th>
                                <th>Name</th>
                                <th>UserId</th>
                                <th>License GUID</th>
                              </tr>
                            </thead>
                            <tbody>
                              {result.data.created.map((emp, index) => (
                                <tr key={index}>
                                  <td>{emp.row}</td>
                                  <td>{emp.email}</td>
                                  <td>{emp.name}</td>
                                  <td>{emp.userId}</td>
                                  <td>{emp.licenseGuid}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}

                    {result.data &&
                      result.data.errors &&
                      result.data.errors.length > 0 && (
                        <div className="mt-3">
                          <h5>Errors:</h5>
                          <Table striped bordered size="sm">
                            <thead>
                              <tr>
                                <th>Row</th>
                                <th>Email</th>
                                <th>Error</th>
                              </tr>
                            </thead>
                            <tbody>
                              {result.data.errors.map((err, index) => (
                                <tr key={index}>
                                  <td>{err.row}</td>
                                  <td>{err.email}</td>
                                  <td>{err.error}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container> */}
    </>
  );
};
